import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { ShieldCheck, KeyRound } from 'lucide-react';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import { pickWelcomeBackground } from '@/lib/branding';

const GOOGLE_ERROR_MESSAGES = {
  email_unverified: 'Your Google account email is not verified. Verify it with Google, then try again.',
  google_disabled: 'Google sign-in is temporarily unavailable.',
  invalid_state: 'That Google sign-in link expired. Please try again.',
  expired_state: 'That Google sign-in link expired. Please try again.',
  state_race: 'That Google sign-in was already used. Please try again.',
  token_exchange: "We couldn't finish signing you in with Google. Please try again.",
  token_network: "We couldn't reach Google. Please try again in a moment.",
  id_token_invalid: "Google's response could not be verified. Please try again.",
  issuer: "Google's response could not be verified.",
  nonce: "Google's response could not be verified.",
  missing_claims: 'Google did not return the required account info.',
  missing_code: 'Google sign-in was cancelled.',
  link_failed: 'We could not link your Google account. Please try again.',
  access_denied: 'You cancelled Google sign-in.',
};

function formatApiError(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d.length && d[0]?.msg) return d[0].msg;
  return fallback;
}

export default function Login() {
  const { login, verify2FA } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null); // { challenge_token, source? }
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [useBackup, setUseBackup] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [mode, setMode] = useState(params.get('mode') === 'signin' ? 'form' : 'welcome'); // 'welcome' | 'form'
  const [bg] = useState(pickWelcomeBackground);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Is Google sign-in configured on this deployment? Gates the Google trigger.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await http.get('/auth/google/status');
        if (!cancelled) setGoogleEnabled(!!r.data?.enabled);
      } catch { if (!cancelled) setGoogleEnabled(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Handle post-Google redirects (?error=... and ?google_2fa=1)
  useEffect(() => {
    const err = params.get('error');
    if (err) {
      toast.error(GOOGLE_ERROR_MESSAGES[err] || 'Google sign-in failed. Please try again.');
      const p = new URLSearchParams(params); p.delete('error'); setParams(p, { replace: true });
    }
    const g2fa = params.get('google_2fa');
    if (g2fa === '1') {
      (async () => {
        try {
          const r = await http.get('/auth/google/2fa-token');
          const tok = r.data?.challenge_token;
          if (tok) {
            setChallenge({ challenge_token: tok, source: 'google' });
            const p = new URLSearchParams(params); p.delete('google_2fa'); setParams(p, { replace: true });
            toast.info('Enter your 2FA code to finish signing in with Google.');
          }
        } catch {
          toast.error('That Google sign-in expired. Please try again.');
        }
      })();
    }
    // On-mount check for Google 2FA redirect — reading `params` via closure is
    // intentional; adding it would re-fire on every URL param change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result && result.requires_2fa) {
        setChallenge(result);
        toast.info('Enter your 2FA code to continue.');
      } else {
        toast.success(`Welcome back, ${(result.name || '').split(' ')[0] || 'there'}`);
        nav(result.onboarded ? '/app' : '/onboarding');
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Login failed'));
    } finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (!challenge) return;
    setLoading(true);
    try {
      const u = await verify2FA(challenge.challenge_token, code.trim(), rememberDevice);
      toast.success(`Welcome back, ${(u.name || '').split(' ')[0] || 'there'}`);
      nav(u.onboarded ? '/app' : '/onboarding');
    } catch (err) {
      toast.error(formatApiError(err, 'Invalid code — try the next one.'));
    } finally { setLoading(false); }
  };

  const showWelcome = !challenge && mode === 'welcome';
  return (
    <div className="min-h-screen bg-navy relative overflow-hidden" data-testid="login-page">
      {/* Blurred fill lets the whole photo show without cropping the subject */}
      <img
        src={bg}
        alt=""
        aria-hidden="true"
        className={`absolute inset-0 w-full h-full object-cover scale-125 blur-2xl transition-opacity duration-[1200ms] ease-out ${imgLoaded ? 'opacity-70' : 'opacity-0'}`}
      />
      <img
        src={bg}
        alt=""
        onLoad={() => setImgLoaded(true)}
        className={`absolute inset-0 w-full h-full object-center animate-kenburns ${showWelcome ? 'object-contain' : 'object-cover'} transition-opacity duration-[1200ms] ease-out ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
      />
      {showWelcome ? (
        <div className="absolute inset-0 flex flex-col" data-testid="welcome-screen">
          {/* Soft gradients for text readability — no zoom, image stays intact */}
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-navy/70 to-transparent pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/10 pointer-events-none" />

          {/* Top bar: logo (→ landing) + Sign In */}
          <div className="relative z-10 flex items-center justify-between px-6 lg:px-14 pt-7">
            <button type="button" data-testid="welcome-home-logo" onClick={() => nav('/')}
              className="flex items-center gap-2.5 group" aria-label="Back to home">
              <span className="w-10 h-10 rounded-xl bg-white/12 backdrop-blur-sm text-white flex items-center justify-center font-heading text-lg group-hover:bg-white/20 transition-smooth">B<span className="text-gold">+</span></span>
              <span className="font-heading text-xl tracking-tight text-white">Brotherhood<span className="text-gold">+</span></span>
            </button>
            <button type="button" data-testid="welcome-signin-link" onClick={() => setMode('form')}
              className="text-sm text-white/85 hover:text-white font-medium transition-smooth">Sign In</button>
          </div>

          {/* Content anchored bottom */}
          <div className="relative z-10 mt-auto w-full max-w-md lg:max-w-2xl mx-auto lg:mx-0 lg:ml-14 px-7 lg:pr-12 pb-11 lg:pb-16">
            <p className="text-[11px] uppercase tracking-[0.32em] text-gold font-semibold mb-4">Brotherhood+</p>
            <h1 className="font-heading text-white tracking-[-0.01em] leading-[1.06] lg:leading-[1.02] text-[clamp(2.05rem,4.4vw,3.4rem)] [text-wrap:balance]">
              <span className="lg:whitespace-nowrap">Become the man</span>{' '}
              <span className="lg:whitespace-nowrap">you&apos;re meant to be.</span>
            </h1>
            <p className="text-white/80 text-base lg:text-lg leading-relaxed mt-5 max-w-md">Brotherhood+ is where men come to do the inner work — guided learning, daily reflection, a coach in your corner, and brothers who get it. Rooted in culture from the start.</p>
            <div className="mt-8 max-w-md">
              <div className="space-y-3">
                <button type="button" data-testid="welcome-create-account" onClick={() => nav('/register')}
                  className="w-full bg-gold text-navy rounded-full py-4 font-semibold hover:-translate-y-0.5 transition-smooth shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
                  Create Account
                </button>
                {googleEnabled && (
                  <GoogleSignInButton
                    label="Continue with Google"
                    nextUrl="/app"
                    testId="welcome-google-btn"
                    className="w-full bg-white/10 backdrop-blur-md border border-white/25 text-white rounded-full py-4 font-semibold hover:bg-white/20 transition-smooth flex items-center justify-center gap-3"
                  />
                )}
              </div>
              <div className="mt-5 space-y-2">
                {googleEnabled && (
                  <p className="text-white/60 text-xs" data-testid="welcome-google-plan-hint">
                    New to Brotherhood+?{' '}
                    <button type="button" data-testid="welcome-google-hint-register" onClick={() => nav('/register')}
                      className="text-gold/90 hover:text-gold font-medium underline underline-offset-2">Create an account</button>{' '}to choose your plan.
                  </p>
                )}
                <p className="text-white/75 text-sm">Already have an account?{' '}
                  <button type="button" data-testid="welcome-footer-signin" onClick={() => setMode('form')}
                    className="text-gold font-semibold hover:underline">Sign In</button>
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
      <div className="min-h-screen flex items-center justify-center p-6 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-navy/55 via-navy/40 to-navy/80" />
      <div className="relative w-full max-w-md">
        <button type="button" data-testid="login-back"
          onClick={() => { if (challenge) { setChallenge(null); setCode(''); } else { setMode('welcome'); } }}
          className="text-sm text-white/70 hover:text-white">← Back</button>
        <button type="button" data-testid="login-home-logo" onClick={() => nav('/')}
          className="mt-6 mx-auto flex items-center justify-center gap-2.5 group" aria-label="Back to home">
          <span className="w-9 h-9 rounded-2xl bg-white/10 backdrop-blur-md text-white flex items-center justify-center font-heading text-base group-hover:bg-white/20 transition-smooth">B<span className="text-gold">+</span></span>
          <span className="font-heading text-xl tracking-tight text-white bh-goldshine">Brotherhood<span className="text-gold">+</span></span>
        </button>
        <div className="mt-5 bg-white rounded-3xl p-8 card-soft">
          {!challenge ? (
            <>
              <h1 className="font-heading text-3xl mb-6">Sign in</h1>
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-navy/60">Email</label>
                  <input data-testid="login-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                    className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-navy/60">Password</label>
                  <input data-testid="login-password" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
                    className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" />
                </div>
                <button data-testid="login-submit" disabled={loading}
                  className="w-full bg-navy text-white rounded-full py-4 font-medium hover:bg-navy-soft transition-smooth disabled:opacity-50">
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
              </form>
              <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-widest text-navy/40">
                <span className="flex-1 h-px bg-navy/10" />
                <span>or</span>
                <span className="flex-1 h-px bg-navy/10" />
              </div>
              {googleEnabled && (
                <GoogleSignInButton
                  label="Continue with Google"
                  nextUrl="/app"
                  testId="login-google-btn"
                />
              )}
              <p className="text-sm text-navy/60 mt-6 text-center" data-testid="login-google-plan-hint">
                New to Brotherhood+?{' '}
                <Link to="/register" data-testid="login-to-register" className="text-navy font-medium">Create an account</Link>{' '}to choose your plan.
              </p>
              <p className="text-xs text-navy/50 mt-2 text-center">
                <Link to="/forgot-password" data-testid="login-forgot-link" className="hover:text-navy">Forgot password?</Link>
              </p>
            </>
          ) : (
            <div data-testid="login-2fa-step">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-gold/15 text-gold flex items-center justify-center">
                  <ShieldCheck size={20}/>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-gold font-semibold">Two-factor auth</p>
                  <h1 className="font-heading text-2xl leading-tight">Enter your code</h1>
                </div>
              </div>
              <p className="text-sm text-navy/60 mb-5">
                {useBackup
                  ? 'Enter one of your 10 backup codes (case-insensitive).'
                  : 'Enter the 6-digit code from your authenticator app.'}
              </p>
              <form onSubmit={submitCode} className="space-y-3">
                <input
                  autoFocus
                  inputMode={useBackup ? 'text' : 'numeric'}
                  pattern={useBackup ? undefined : '[0-9]*'}
                  value={code}
                  onChange={(e) => setCode(useBackup ? e.target.value.toUpperCase().slice(0, 12) : e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  placeholder={useBackup ? 'XXXXX-XXXXX' : '000000'}
                  className="w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold tracking-[0.4em] font-mono text-center text-lg"
                  data-testid="login-2fa-input"
                />
                <label className="flex items-center gap-2 text-sm text-navy/70">
                  <input type="checkbox" checked={rememberDevice} onChange={(e)=>setRememberDevice(e.target.checked)}
                    className="rounded" data-testid="login-2fa-remember"/>
                  Remember this device for 30 days
                </label>
                <button type="submit" disabled={loading || !code}
                  className="w-full rounded-full bg-navy text-white py-3 font-medium hover:bg-navy/90 transition-smooth disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="login-2fa-submit">
                  <KeyRound size={14}/> {loading ? 'Verifying…' : 'Verify & sign in'}
                </button>
              </form>
              <div className="mt-4 flex items-center justify-between text-xs">
                <button onClick={() => { setUseBackup(u => !u); setCode(''); }}
                  className="text-navy/60 hover:text-navy" data-testid="login-2fa-toggle-backup">
                  {useBackup ? '← Use authenticator code' : 'Lost access? Use a backup code'}
                </button>
                <button onClick={() => { setChallenge(null); setCode(''); }}
                  className="text-navy/50 hover:text-navy" data-testid="login-2fa-cancel">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
      )}
    </div>
  );
}
